import React, { Dispatch, SetStateAction } from "react";
import { Checkbox, Space, Switch, Typography } from "antd";
import { CheckboxValueType } from "antd/es/checkbox/Group";

// styles
import * as S from "./styles";

import { Common } from "@secondcloset/types";
type RecommendedShippingMethod = Exclude<Common.Organization["recommended_shipping_methods"], undefined>;
type ServiceTier = Exclude<RecommendedShippingMethod["sc_service_tier"], undefined>;

interface ShipmentMethodSectionProps {
  enabledShippingMethod: RecommendedShippingMethod;
  selectedSCServiceTier: ServiceTier;
  onEnabledShippingMethodChange: Dispatch<SetStateAction<RecommendedShippingMethod>>;
  onSelectedSCServiceTierChange: Dispatch<SetStateAction<ServiceTier>>;
}

export enum ShippingMethods {
  parcel = "parcel",
  freight = "freight",
  sc_last_mile = "sc_last_mile",
}

export enum SCServiceTiers {
  threshold = "threshold",
  room_of_choice = "room_of_choice",
  white_glove = "white_glove",
  to_the_door = "to_the_door",
}

const { Text } = Typography;

const ShipmentMethodSection: React.FC<ShipmentMethodSectionProps> = ({
  enabledShippingMethod,
  selectedSCServiceTier,
  onEnabledShippingMethodChange,
  onSelectedSCServiceTierChange,
}) => {
  const scServiceTierOptions = [
    { label: "Threshold", value: SCServiceTiers.threshold },
    { label: "Room Of Choice", value: SCServiceTiers.room_of_choice },
    { label: "White Glove", value: SCServiceTiers.white_glove },
    { label: "To The Door", value: SCServiceTiers.to_the_door },
  ];

  const handleSelectedServiceTierChange = (values: CheckboxValueType[]) => {
    if (!values.length) {
      onEnabledShippingMethodChange((method) => ({
        ...method,
        [ShippingMethods.sc_last_mile]: false,
      }));
    }
    const selectedTiers = scServiceTierOptions.reduce(
      (acc, cur) => ({ ...acc, [cur.value]: values.includes(cur.value) }),
      {} as ServiceTier
    );
    onSelectedSCServiceTierChange(selectedTiers);
  };

  const handleShippingMethodChange = (method: ShippingMethods, value: boolean) => {
    if (method === ShippingMethods.sc_last_mile) {
      onSelectedSCServiceTierChange((selectedTier) =>
        Object.keys(selectedTier).reduce((acc, cur) => ({ ...acc, [cur]: true }), {} as ServiceTier)
      );
    }
    onEnabledShippingMethodChange((enabledMethod) => ({
      ...enabledMethod,
      [method]: value,
    }));
  };

  const getSelectedServiceTierAsArray = () => {
    if (!selectedSCServiceTier) return [];
    return Object.keys(selectedSCServiceTier).filter((tier) => selectedSCServiceTier[tier as SCServiceTiers]);
  };

  const renderShippingMethodSwitch = (method: ShippingMethods) => (
    <Switch onChange={(value) => handleShippingMethodChange(method, value)} checked={enabledShippingMethod[method]} />
  );

  const buildServiceTierOptions = () => {
    if (!enabledShippingMethod.sc_last_mile) return null;
    return (
      <S.Wrapper>
        <S.Header>Set Up GoBolt Freight Service</S.Header>
        <Text type="secondary">
          Service tier options are available to select when last mile method is activated. Only checked options will be
          visible to user when creating a shipment.
        </Text>
        <S.Row>
          <Space direction="vertical">
            <Text strong>GOBOLT SERVICE TIER</Text>
            <Checkbox.Group
              options={scServiceTierOptions}
              value={getSelectedServiceTierAsArray()}
              onChange={handleSelectedServiceTierChange}
              style={{ display: "flex", flexDirection: "column" }}
            />
          </Space>
        </S.Row>
      </S.Wrapper>
    );
  };

  return (
    <>
      <S.Wrapper>
        <S.Header>Recommended Shipment Method</S.Header>
        <Text type="secondary">
          Shipment methods will be recommended to the user based on shipment details (package and destination) and the
          below configuration. All shipment options are visible to the user when creating a shipment, but disabling an
          option will ensure it is never the recommendation for the user. Activate shipment methods that business client
          prefers:
        </Text>
        <S.Row>
          <Text strong>Parcel Options (e.g FedEx, UPS, ...etc)</Text>
          {renderShippingMethodSwitch(ShippingMethods.parcel)}
        </S.Row>
        <S.Row>
          <Text strong>3rd Party Freight (e.g Freightcom Inc)</Text>
          {renderShippingMethodSwitch(ShippingMethods.freight)}
        </S.Row>
        <S.Row>
          <Text strong>GoBolt Freight (Big & Bulky)</Text>
          {renderShippingMethodSwitch(ShippingMethods.sc_last_mile)}
        </S.Row>
      </S.Wrapper>
      {buildServiceTierOptions()}
    </>
  );
};

export default ShipmentMethodSection;
